// Event-effect dispatcher. Walks an `EventOutcome.effects` list, calls
// shared's pure `applyEventEffect` reducer for the state mutation parts,
// and resolves the side-effect signals (`grantsBoon` / grant-item / etc.)
// against client-side catalogues (`pickBoon`, `generateItem`).
//
// Pure: no I/O, no store mutation. The active-character store wraps the
// returned `run` + `gainedItems` + `goldDelta` into a single
// `persistDungeonRun` call so the event resolution rides one atomic save.
//
// Phase 3 Step 10c-event-treasure.

import type {
  Rng} from '@renavora/shared';
import {
  applyEventEffect,
  pickBoon,
  type DungeonBoonState,
  type DungeonRunState,
  type DungeonTemplate,
  type EventOutcome,
  type Item,
  type NodeCoord,
} from '@renavora/shared';
import { generateItem } from '@renavora/content';

/**
 * Maximum simultaneous run-scope boons (ADR 0026 §2.6 / ADR 0030 §2.3.3).
 * Duplicated from active-character.ts so this dispatcher stays free of
 * a back-reference to the store module.
 */
const MAX_ACTIVE_BOONS = 3;

/** Ilvl ceiling used by event grant-item, mirroring dungeon-loot.ts §10d. */
const ILVL_CEILING = 500;

/**
 * FIFO-push a granted boon onto `activeBoons` capped at MAX_ACTIVE_BOONS.
 * Mirrors `applyBoonGrant` in active-character.ts; both inline a 3-line
 * helper rather than reach across module boundaries.
 */
function pushBoon(
  active: readonly DungeonBoonState[],
  granted: DungeonBoonState,
): readonly DungeonBoonState[] {
  if (active.length < MAX_ACTIVE_BOONS) return [...active, granted];
  return [...active.slice(1), granted];
}

export interface EventDispatchContext {
  readonly run: DungeonRunState;
  readonly template: DungeonTemplate;
  /** Map seed from `parseMapId(...).seed`; routed to pickBoon + generateItem. */
  readonly runSeed: number;
  /**
   * Event node coordinate; used to derive the boon roll's seed so the same
   * `(runSeed, coord)` always rolls the same boon (matches camp/boon).
   */
  readonly coord: NodeCoord;
  /**
   * Rng owned by the caller. We pull `nextU32`s off it for in-effect
   * sub-rolls (grant-item etc.) so re-running the same outcome against
   * the same input is byte-for-byte identical.
   */
  readonly rng: Rng;
}

export interface EventDispatchResult {
  /** Run state with hp-delta / penalty-pct / apply-debuff / grant-boon applied. */
  readonly run: DungeonRunState;
  /** Sum of all `gold-delta` amounts to credit to character.gold. */
  readonly goldDelta: number;
  /** Items the caller should push onto character.inventory. */
  readonly gainedItems: readonly Item[];
  /**
   * One short label per effect (from `applyEventEffect.displayLabel`),
   * plus boon-id / item-id appended where relevant. Caller surfaces
   * these in the post-outcome modal.
   */
  readonly displayMessages: readonly string[];
}

/**
 * Apply every effect in an outcome to the run state. The `applyEventEffect`
 * reducer covers the pure mutations (hp-delta clamp / penalty-pct clamp /
 * pendingDebuffs push); this dispatcher fans out the side-effect signals:
 *
 *   - `grantsBoon=true` → `pickBoon` against the current run + boon push
 *   - `grant-item` effect → `generateItem` via shared loot ctx + inventory
 *     gather (rarityHint is forwarded but not interpreted, ADR 0030 §2.10)
 *   - `gold-delta` → accumulated into the result for the caller to apply
 *
 * Ordering matters: effects fire in the catalogue-declared order, and
 * each effect sees the run state produced by the previous one (e.g. a
 * grant-boon after an hp-delta sees the clamped HP).
 */
export function dispatchEventOutcome(
  outcome: EventOutcome,
  ctx: EventDispatchContext,
): EventDispatchResult {
  let run = ctx.run;
  let goldDelta = 0;
  const gainedItems: Item[] = [];
  const messages: string[] = [];

  for (const effect of outcome.effects) {
    const step = applyEventEffect(run, effect);
    run = step.run;
    goldDelta += step.goldDelta;
    messages.push(step.displayLabel);

    if (step.grantsBoon) {
      const granted = pickBoon({
        runSeed: ctx.runSeed,
        coord: ctx.coord,
        activeBoons: run.activeBoons,
      });
      if (granted !== null) {
        run = { ...run, activeBoons: pushBoon(run.activeBoons, granted) };
        messages.push(`+ boon ${granted.id}`);
      }
    }

    if (effect.kind === 'grant-item') {
      const item = generateItem(
        {
          dungeonTier: ctx.template.tier,
          difficultyIlvl: Math.min(ILVL_CEILING, ctx.template.baseEnemyLevel),
        },
        ctx.rng,
      );
      gainedItems.push(item);
      messages.push(`+ item ${item.baseId} (${item.rarity})`);
    }
  }

  return { run, goldDelta, gainedItems, displayMessages: messages };
}
